import { createSelector } from 'reselect';
import clone from 'ramda/src/clone';
import descend from 'ramda/src/descend';
import filter from 'ramda/src/filter';
import findIndex from 'ramda/src/findIndex';
import path from 'ramda/src/path';
import pathOr from 'ramda/src/pathOr';
import propEq from 'ramda/src/propEq';
import sortWith from 'ramda/src/sortWith';
import take from 'ramda/src/take';
import i18n from 'invision-core/src/components/i18n/i18n';
import MetadataConstants from 'invision-core/src/components/metadata/metadata.constants';
import MetadataSelectors from 'invision-core/src/components/metadata/metadata.selectors';
import { DiscountsMetadataMapSelector } from 'invision-core/src/components/metadata/discounts/discounts.selectors';
import { getDiscountTooltipModel } from '../../components/shared/tooltipTemplates/discount.tooltip.helper';
import CustomerCareKeys from '../../locales/keys';
import { SelectedCustomerSelector } from './customer.selectors';
import { ACTIVE_PRODUCTS_TO_DISPLAY, ORDER_ITEM_STATUS, RECENT_PRODUCTS_TO_DISPLAY } from '../constants/dashboard.constants';
import { BILLER_RULE_QUANTITY_PRICE_TYPE } from './../../customercare.constants';
import { PRODUCT_AVAILABLE_DELIVERY_CAPABILITY_ACTIONS, PRODUCT_CLASSIFICATIONS } from 'invision-core/src/constants/product.constants';
import { getPoliciesWithFriendlyNames } from './customer.product.detail.selectors';
import Immutable from 'seamless-immutable';
import { StandaloneProductsMetadataSelector } from './offering.order.selectors';
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  return state.customercare.recoverableUiState.products;
};
export var CustomerProductsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  if (selectedCustomer.products) {
    return selectedCustomer.products.data;
  }
});
export var CustomerProductsCollectionSelector = createSelector([CustomerProductsSelector], function (products) {
  if (products && products.LockerItems) {
    return products.LockerItems;
  } else {
    return null;
  }
});
export var CustomerRecentProductsCollectionSelector = createSelector([CustomerProductsCollectionSelector], function (products) {
  if (products) {
    return products.slice(0, RECENT_PRODUCTS_TO_DISPLAY);
  } else {
    return null;
  }
});
export var ActiveProductSelector = createSelector([SelectedCustomerSelector, CustomerRecentProductsCollectionSelector, MetadataSelectors.codes.MetadataCodeSelector(MetadataConstants.codes.ProductLineOfBusinessDeliveryCapabilityConfigurationAction)], function (selectedCustomer, products, ProductLineOfBusinessDeliveryCapabilityConfigurationAction) {
  if (products && products.length) {
    var activeProduct = selectedCustomer.products.activeProduct || products[0];
    if (ProductLineOfBusinessDeliveryCapabilityConfigurationAction) {
      return activeProduct.setIn(['ProductContext', 'PurchasePolicies'], getPoliciesWithFriendlyNames(activeProduct, ProductLineOfBusinessDeliveryCapabilityConfigurationAction));
    } else {
      return activeProduct;
    }
  } else {
    return null;
  }
});
export var CanViewContentActivitySelector = createSelector([ActiveProductSelector], function (productDetail) {
  return !pathOr(0, ['ProductContext', 'AvailableDeliveryCapabilityActions', 'length'], productDetail) ? false : productDetail.ProductContext.AvailableDeliveryCapabilityActions.some(function (action) {
    return action.Code === PRODUCT_AVAILABLE_DELIVERY_CAPABILITY_ACTIONS.DOWNLOAD || action.Code === PRODUCT_AVAILABLE_DELIVERY_CAPABILITY_ACTIONS.PLAYBACK;
  });
});
export var CustomerProductsCountSelector = createSelector([CustomerProductsSelector], function (products) {
  if (products && products.RecordCount) {
    return products.RecordCount;
  } else {
    return 0;
  }
});
var DEFAULT_TABLE_DATA = [];
export var TableDataSelector = createSelector([CustomerProductsCollectionSelector], function (productsCollection) {
  if (productsCollection) {
    return productsCollection.map(function (product) {
      return Object.assign({}, product, {
        Class: productClassConverter(product),
        Offer: product.OfferName,
        OrderDate: product.Created,
        PricingPlan: pathOr({}, ['PricingPlan'], product),
        ProductName: pathOr('', ['Product', 'Name'], product),
        Service: pathOr('', ['PricingPlan', 'ServiceTiers', 0, 'ServiceName'], product)
      });
    });
  }
  return DEFAULT_TABLE_DATA;
});
var productClassConverter = function productClassConverter(productObject) {
  var classification = pathOr('', ['Product', 'ProductClassification'], productObject);
  switch (classification) {
    case PRODUCT_CLASSIFICATIONS.NONE:
      return i18n.translate(CustomerCareKeys.NONE);
    case PRODUCT_CLASSIFICATIONS.PRODUCT:
      return i18n.translate(CustomerCareKeys.PRODUCT);
    case PRODUCT_CLASSIFICATIONS.SERVICE_FEATURE:
      return i18n.translate(CustomerCareKeys.SERVICE_FEATURE);
    case PRODUCT_CLASSIFICATIONS.AD_HOC:
      return i18n.translate(CustomerCareKeys.AD_HOC);
    default:
      return null;
  }
};
export var IncludeExpiredSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.includeExpired;
});
export var IncludeRemovedSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.includeRemoved;
});
export var IncludeSearchSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.includeSearch;
});
export var SelectedPageSizePreference = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.pageSizePreference;
});
export var ProductClassificationSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.filterData.classification ? [uiState.filterData.classification] : null;
});
export var IsFetchingProductsDataSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  if (selectedCustomer.products) {
    return selectedCustomer.products.isFetchingData;
  }
});
export var IsRecentProductsDataSelector = createSelector([CustomerProductsSelector], function (products) {
  if (products) {
    return products.RecordCount > RECENT_PRODUCTS_TO_DISPLAY && products.LockerItems.length <= RECENT_PRODUCTS_TO_DISPLAY;
  } else {
    return false;
  }
});
export var CurrentPageNumberSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.pageNumber || 0;
});
export var RecordCountSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.products.recordCount || 0;
});
export var TotalPagesSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.products.pageCount;
});
export var IsRemovingProductSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return selectedCustomer.products ? selectedCustomer.products.isRemovingProduct : null;
});
export var SortCriteriaSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(null, ['filterData', 'sortCriteria'], uiState);
});
export var CustomerActiveProductsSelector = createSelector([SelectedCustomerSelector, StandaloneProductsMetadataSelector, DiscountsMetadataMapSelector], function (selectedCustomer, standalonesProductsMetadata, discountsMetadataMap) {
  var standaloneProducts = pathOr([], ['products', 'activeProducts'], selectedCustomer);
  var allOrderItems = standaloneProducts.reduce(function (result, standalone) {
    var product = standalonesProductsMetadata[standalone.ProductId];
    if (product) {
      var activeProductPricingPlan = product.PricingPlans.find(function (pricingPlan) {
        return pricingPlan.Id === standalone.PricingPlanId;
      });
      result.push({
        AdHocOverrideDetails: standalone.InvoiceText || standalone.Amount || standalone.Amount === 0 ? {
          Amount: standalone.Amount,
          InvoiceText: standalone.InvoiceText
        } : null,
        Amount: standalone.Amount || 0,
        BillerRulePrices: standalone.BillerRulePrices,
        BillerTypeAmounts: standalone.BillerTypeAmounts,
        Connected: standalone.ConnectedDate,
        DiscountAmount: standalone.DiscountAmount || 0,
        Discounts: (standalone.Discounts || []).map(function (discount) {
          return getDiscountTooltipModel(discount, activeProductPricingPlan.Currency, discountsMetadataMap);
        }),
        GrossAmount: standalone.TotalAmount || 0,
        Id: standalone.OrderItemId,
        IsStandalone: product.Standalone,
        LockerItemId: standalone.SubscriberProductId,
        OfferingOptionPriceId: standalone.OfferingOptionPriceId,
        Product: product,
        PricingPlan: activeProductPricingPlan,
        Status: standalone.Status,
        SubscriberProductExpiration: standalone.SubscriberProductExpiration,
        Quantity: standalone.BulkQuantity
      });
    }
    return result;
  }, []);
  return filter(function (item) {
    return item.Status === ORDER_ITEM_STATUS.ACTIVE && (
    // Only show ACTIVE StandAlone Products and Adhoc products with BillerTypeAmounts Populated(Temporary Fix)

    item.Product.ProductClassification === PRODUCT_CLASSIFICATIONS.PRODUCT || item.Product.ProductClassification === PRODUCT_CLASSIFICATIONS.AD_HOC && item.BillerTypeAmounts && item.BillerTypeAmounts.length || item.PricingPlan.BillerRuleInstances.length === 0);
  }, allOrderItems);
});
export var CustomerActiveProductsDisplayItemsSelector = createSelector([CustomerActiveProductsSelector], function (activeProducts) {
  return activeProducts && activeProducts.length ? getMappedActiveProducts(activeProducts) : null;
});
var getMappedActiveProducts = function getMappedActiveProducts(activeProducts) {
  var getAdHocOverrideAmount = function getAdHocOverrideAmount(pricingPlan) {
    // Checking the first BillerRuleInstances in the array because an adHoc product can only have one Bri.
    return pathOr(null, ['BillerRuleInstances', 0, 'Amount'], pricingPlan);
  };
  var dbss = clone(activeProducts).filter(function (_ref) {
    var _ref$PricingPlan$Bill = _ref.PricingPlan.BillerRuleInstances,
      BillerRuleInstances = _ref$PricingPlan$Bill === void 0 ? [] : _ref$PricingPlan$Bill;
    return BillerRuleInstances.length;
  });
  var ott = clone(activeProducts).filter(function (_ref2) {
    var _ref2$PricingPlan$Bil = _ref2.PricingPlan.BillerRuleInstances,
      BillerRuleInstances = _ref2$PricingPlan$Bil === void 0 ? [] : _ref2$PricingPlan$Bil;
    return !BillerRuleInstances.length;
  });
  var mappedOtt = ott.map(function (_ref3) {
    var Id = _ref3.Id,
      LockerItemId = _ref3.LockerItemId,
      PricingPlan = _ref3.PricingPlan,
      Product = _ref3.Product,
      GrossAmount = _ref3.GrossAmount,
      DiscountAmount = _ref3.DiscountAmount;
    return {
      Id: Id,
      LockerItemId: LockerItemId,
      PricingPlan: PricingPlan,
      Product: Product,
      Quantity: 1,
      Totals: {
        Sum: GrossAmount,
        Discount: DiscountAmount
      }
    };
  });
  var getProductTotalQuantity = function getProductTotalQuantity(OfferingOptionPriceId) {
    var items = filter(propEq(OfferingOptionPriceId, 'OfferingOptionPriceId'))(dbss);
    return items.length;
  };
  var getProductIncrementalQuantity = function getProductIncrementalQuantity(OfferingOptionPriceId, Id) {
    return findIndex(propEq(Id, 'Id'), filter(propEq(OfferingOptionPriceId, 'OfferingOptionPriceId'))(dbss)) + 1;
  };
  var getProductTotalAndDiscount = function getProductTotalAndDiscount(bris, bulkDetails, itemTotalQuantity, itemIncrementalQuantity) {
    // TODO This front end math needs to be removed
    return bris.reduce(function (prev, _ref4) {
      var DiscountAmount = _ref4.DiscountAmount,
        Amount = _ref4.Amount,
        Bulk = _ref4.Bulk,
        QuantityPricingTypeCode = _ref4.QuantityPricingTypeCode,
        StartQuantity = _ref4.StartQuantity,
        EndQuantity = _ref4.EndQuantity,
        BillerRuleConfigurationId = _ref4.BillerRuleConfigurationId;
      var sum = 0;
      if (Bulk) {
        sum = pathOr(0, ['Amount'], bulkDetails.find(function (item) {
          return item.BillerRuleConfigurationId === BillerRuleConfigurationId;
        }));
      } else {
        if (QuantityPricingTypeCode === BILLER_RULE_QUANTITY_PRICE_TYPE.TIERED) {
          sum = itemIncrementalQuantity >= StartQuantity && itemIncrementalQuantity <= EndQuantity ? Amount : 0;
        } else if (QuantityPricingTypeCode === BILLER_RULE_QUANTITY_PRICE_TYPE.TAPERED) {
          sum = itemTotalQuantity >= StartQuantity && itemTotalQuantity <= EndQuantity ? Amount : 0;
        } else {
          sum = Amount;
        }
      }
      return Object.assign(prev, {
        Sum: prev.Sum + sum,
        Discount: prev.Discount + DiscountAmount
      });
    }, {
      Sum: 0,
      Discount: 0
    });
  };
  var mappedDbss = dbss.map(function (_ref5) {
    var AdHocOverrideDetails = _ref5.AdHocOverrideDetails,
      Id = _ref5.Id,
      LockerItemId = _ref5.LockerItemId,
      OfferingOptionPriceId = _ref5.OfferingOptionPriceId,
      PricingPlan = _ref5.PricingPlan,
      Product = _ref5.Product,
      Connected = _ref5.Connected,
      Quantity = _ref5.Quantity,
      _ref5$BillerRulePrice = _ref5.BillerRulePrices,
      BillerRulePrices = _ref5$BillerRulePrice === void 0 ? [] : _ref5$BillerRulePrice,
      SubscriberProductExpiration = _ref5.SubscriberProductExpiration,
      Discounts = _ref5.Discounts;
    PricingPlan.BillerRuleInstances = PricingPlan.BillerRuleInstances.filter(function (bri) {
      return bri.Amount !== null;
    });
    var itemTotalQuantity = getProductTotalQuantity(OfferingOptionPriceId);
    var itemIncrementalQuantity = getProductIncrementalQuantity(OfferingOptionPriceId, Id);
    var defaultOverrideDetails = {
      Amount: getAdHocOverrideAmount(PricingPlan)
    };
    return {
      AdHocOverrideDetails: AdHocOverrideDetails ? Object.assign({}, defaultOverrideDetails, AdHocOverrideDetails) : AdHocOverrideDetails,
      Discounts: Discounts,
      Id: Id,
      LockerItemId: LockerItemId,
      PricingPlan: PricingPlan,
      Product: Product,
      Quantity: Quantity,
      Totals: getProductTotalAndDiscount(PricingPlan.BillerRuleInstances, BillerRulePrices, itemTotalQuantity, itemIncrementalQuantity),
      Dates: {
        Connected: Connected
      },
      SubscriberProductExpiration: SubscriberProductExpiration
    };
  });
  return take(ACTIVE_PRODUCTS_TO_DISPLAY, sortWith([descend(path(['Dates', 'Ordered']))], mappedDbss.concat(mappedOtt)));
};
export var IsFetchingActiveProductsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  if (selectedCustomer.products) {
    return selectedCustomer.products.isFetchingActiveProductsData;
  }
});
export var RemoveProductExecutionOptionsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  return Immutable(selectedCustomer.products.executionOptions);
});